import React, { useMemo, useState } from 'react';
import {
  computeTradeWindowEvaluation,
  EvaluationCountPreset,
  formatEvaluationReport,
  formatMoneySigned,
  formatPercentSigned,
  formatPnlDisplay,
  formatTradeMoment,
  TradeForEvaluation,
} from '../../utils/tradeWindowEvaluation';
import SideBreakdownSummary from './SideBreakdownSummary';

const COUNT_OPTIONS: { value: EvaluationCountPreset; label: string }[] = [
  { value: 5, label: 'Last 5' },
  { value: 10, label: 'Last 10' },
  { value: 12, label: 'Last 12' },
  { value: 20, label: 'Last 20' },
  { value: 'all', label: 'All loaded' },
];

export interface TradeEvaluationPanelProps {
  trades: TradeForEvaluation[];
  /** Strategy config fixedOrderAmount — enables return-on-order-size metric */
  fixedOrderAmount?: number;
  /** True when KuCoin pagination has more pages not yet loaded */
  hasMoreRemote?: boolean;
  defaultCount?: EvaluationCountPreset;
}

const TradeEvaluationPanel: React.FC<TradeEvaluationPanelProps> = ({
  trades,
  fixedOrderAmount,
  hasMoreRemote = false,
  defaultCount = 12,
}) => {
  const [countPreset, setCountPreset] = useState<EvaluationCountPreset>(defaultCount);
  const [expanded, setExpanded] = useState(true);
  const [showObservations, setShowObservations] = useState(false);
  const [copyHint, setCopyHint] = useState<string | null>(null);

  const evaluation = useMemo(
    () => computeTradeWindowEvaluation(trades, countPreset, { fixedOrderAmount }),
    [trades, countPreset, fixedOrderAmount]
  );

  const handleCopy = async () => {
    if (!evaluation) return;
    try {
      await navigator.clipboard.writeText(formatEvaluationReport(evaluation));
      setCopyHint('Copied');
      setTimeout(() => setCopyHint(null), 2000);
    } catch {
      setCopyHint('Copy failed');
      setTimeout(() => setCopyHint(null), 2000);
    }
  };

  if (!trades.length) {
    return (
      <div
        style={{
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 16,
          fontSize: 13,
          color: '#6c757d',
        }}
      >
        Trade evaluation will appear when closed trades are loaded.
      </div>
    );
  }

  if (!evaluation) return null;

  const pnlColor = (n: number) => (n >= 0 ? '#198754' : '#dc3545');

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #c5daf0',
        borderRadius: 8,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
          padding: '10px 14px',
          background: '#f0f7ff',
          borderBottom: expanded ? '1px solid #c5daf0' : 'none',
        }}
      >
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            fontSize: 14,
            fontWeight: 600,
            color: '#212529',
          }}
        >
          <span style={{ fontSize: 11, color: '#5c7fa5' }}>{expanded ? '▼' : '▶'}</span>
          {evaluation.title}
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              background: '#fff',
              border: '1px solid #dee2e6',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            {COUNT_OPTIONS.map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => setCountPreset(opt.value)}
                style={{
                  padding: '6px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  border: 0,
                  borderRight: '1px solid #dee2e6',
                  cursor: 'pointer',
                  background: countPreset === opt.value ? '#086afb' : '#fff',
                  color: countPreset === opt.value ? '#fff' : '#495057',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleCopy}
            title="Copy evaluation as text"
            style={{
              padding: '6px 12px',
              fontSize: 11,
              fontWeight: 600,
              border: '1px solid #086afb',
              borderRadius: 6,
              background: '#fff',
              color: '#086afb',
              cursor: 'pointer',
            }}
          >
            {copyHint || 'Copy report'}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '12px 14px' }}>
          {(hasMoreRemote && countPreset === 'all') && (
            <div
              style={{
                fontSize: 12,
                color: '#856404',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: 6,
                padding: '8px 10px',
                marginBottom: 12,
              }}
            >
              Based on {evaluation.totalTradesAvailable} trades loaded. Use &quot;Load older trades&quot; below for more history.
            </div>
          )}
          {countPreset !== 'all' && evaluation.totalTradesAvailable > evaluation.actualCount && (
            <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 10 }}>
              Window from {evaluation.totalTradesAvailable} loaded trades (newest first).
            </div>
          )}

          {/* Mini trade table */}
          <div
            style={{
              maxHeight: 200,
              overflowY: 'auto',
              marginBottom: 14,
              border: '1px solid #e9ecef',
              borderRadius: 6,
            }}
          >
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: '#6c757d', fontWeight: 500 }}>
                    Date/Time
                  </th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: '#6c757d', fontWeight: 500 }}>Trade</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', color: '#6c757d', fontWeight: 500 }}>
                    P&L ($)
                  </th>
                </tr>
              </thead>
              <tbody>
                {evaluation.trades.map((t, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                    <td style={{ padding: '6px 10px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                      {t.closedDisplay}
                    </td>
                    <td style={{ padding: '6px 10px', fontWeight: 500, color: '#212529' }}>
                      <TradeDirectionLabel label={t.label} direction={t.direction} />
                    </td>
                    <td
                      style={{
                        padding: '6px 10px',
                        textAlign: 'right',
                        fontWeight: 600,
                        color: pnlColor(t.pnlNet),
                      }}
                    >
                      {formatPnlDisplay(t.pnlNet)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 10,
              marginBottom: 14,
            }}
          >
            <SummaryCell label="Gross P&L (before fees)" value={formatMoneySigned(evaluation.grossPnl)} color={pnlColor(evaluation.grossPnl)} />
            <SummaryCell label="Total fees" value={formatMoneySigned(evaluation.totalFees)} color="#dc3545" />
            <SummaryCell label="Net P&L (after fees)" value={formatMoneySigned(evaluation.netPnl)} color={pnlColor(evaluation.netPnl)} />
            {evaluation.returnOnOrderSize != null && evaluation.orderSize != null ? (
              <SummaryCell
                label={`Return on order ($${evaluation.orderSize.toFixed(0)})`}
                value={formatPercentSigned(evaluation.returnOnOrderSize)}
                color={pnlColor(evaluation.returnOnOrderSize)}
                title={`Net P&L as a percentage of the configured $${evaluation.orderSize.toFixed(2)} order size for this window`}
              />
            ) : (
              <SummaryCell
                label="Return on order size"
                value="—"
                color="#6c757d"
                title="Set a fixed order amount in strategy config to see return on order size"
              />
            )}
            <SummaryCell label="Win rate" value={`${evaluation.winRate.toFixed(1)}%`} color="#086afb" />
            <SummaryCell
              label="W / L"
              value={`${evaluation.winCount} / ${evaluation.lossCount}`}
              color="#212529"
            />
          </div>

          <SideBreakdownSummary breakdown={evaluation.sideBreakdown} />

          {/* Winners / Losers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#198754', marginBottom: 6 }}>Largest Winners</div>
              {evaluation.largestWinners.length ? (
                evaluation.largestWinners.map((t, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#495057', marginBottom: 4 }}>
                    {i + 1}. {t.label} ({formatTradeMoment(t)}) → {formatMoneySigned(t.pnlNet)}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 12, color: '#adb5bd' }}>None</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#dc3545', marginBottom: 6 }}>Largest Losers</div>
              {evaluation.largestLosers.length ? (
                evaluation.largestLosers.map((t, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#495057', marginBottom: 4 }}>
                    {i + 1}. {t.label} ({formatTradeMoment(t)}) → {formatMoneySigned(t.pnlNet)}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 12, color: '#adb5bd' }}>None</div>
              )}
            </div>
          </div>

          {/* Observations toggle */}
          <button
            type="button"
            onClick={() => setShowObservations((s) => !s)}
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#086afb',
              background: 'none',
              border: 0,
              cursor: 'pointer',
              padding: 0,
              marginBottom: showObservations ? 8 : 0,
            }}
          >
            {showObservations ? 'Hide observations' : 'Show observations & suggestions'}
          </button>
          {showObservations && (
            <div style={{ fontSize: 12, color: '#495057', lineHeight: 1.55 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#212529' }}>Observations</div>
              {evaluation.observations.map((o, i) => (
                <p key={`o-${i}`} style={{ margin: '0 0 8px 0' }}>{o}</p>
              ))}
              <div style={{ fontWeight: 600, margin: '12px 0 6px', color: '#212529' }}>What This Suggests</div>
              {evaluation.suggestions.map((s, i) => (
                <p key={`s-${i}`} style={{ margin: s ? '0 0 8px 0' : 0 }}>{s}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function TradeDirectionLabel({
  label,
  direction,
}: {
  label: string;
  direction: 'LONG' | 'SHORT' | 'UNKNOWN';
}) {
  if (direction === 'UNKNOWN') {
    return <span>{label}</span>;
  }
  const base = label.replace(/\s+(LONG|SHORT)$/i, '');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{base}</span>
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: '2px 6px',
          borderRadius: 4,
          background: direction === 'LONG' ? '#d1e7dd' : '#f8d7da',
          color: direction === 'LONG' ? '#0f5132' : '#842029',
        }}
      >
        {direction}
      </span>
    </span>
  );
}

function SummaryCell({
  label,
  value,
  color,
  title,
}: {
  label: string;
  value: string;
  color: string;
  title?: string;
}) {
  return (
    <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '8px 10px' }} title={title}>
      <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

export default TradeEvaluationPanel;
